import React, { useId } from 'react';
import { StyleSheet, View } from 'react-native';
import { Text } from './Texte';
import Svg, { Circle, Defs, LinearGradient, Stop } from 'react-native-svg';
import type { ScoreRingProps } from '../types';
import { colors, typography } from '../theme/tokens';
import { t } from '../i18n';
import { isolerLtr } from '../i18n/sens';

const STROKE_WIDTH = 8;
const STROKE_WIDTH_DEGRADE = 9;
const STROKE_WIDTH_MARQUE = 6;

export function couleurAnneau(score: number): string {
  if (score < 60) return colors.error;
  if (score <= 85) return colors.bluePrimary;
  return colors.green;
}

/** Mélange `hex` avec du blanc : 0 rend la couleur, 1 rend du blanc. */
function eclaircir(hex: string, part: number): string {
  const canaux = [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16));
  return `#${canaux
    .map((c) => Math.round(c + (255 - c) * part).toString(16).padStart(2, '0'))
    .join('')
    .toUpperCase()}`;
}

/**
 * Dégradé de l'anneau : une teinte éclaircie au départ, la couleur de la tranche à l'arrivée. Il
 * habille la règle rouge / bleu / vert sans la remplacer : un score faible doit se voir.
 */
export function degradeAnneau(score: number): [string, string] {
  const fin = couleurAnneau(clamp(score));
  return [eclaircir(fin, 0.4), fin];
}

function clamp(value: number): number {
  return Math.max(0, Math.min(100, Math.round(value)));
}

/** Dégradé de la marque (maquette des offres) : le même pour tous les scores. */
const DEGRADE_MARQUE: [string, string, string] = [colors.cyan, colors.violetLogo, colors.corail];

export function ScoreRing({
  score,
  size = 96,
  variante = 'classique',
  surFond = 'clair',
  legende,
  testID,
}: ScoreRingProps) {
  const value = clamp(score);
  const marque = variante === 'marque';
  const degrade = variante === 'degrade' || marque;
  const sombre = surFond === 'sombre';
  const epaisseur = marque ? STROKE_WIDTH_MARQUE : degrade ? STROKE_WIDTH_DEGRADE : STROKE_WIDTH;
  const radius = (size - epaisseur) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference * (1 - value / 100);
  const [debut, fin] = degradeAnneau(value);
  const arrets: [string, string][] = marque
    ? [
        ['0', DEGRADE_MARQUE[0]],
        ['0.5', DEGRADE_MARQUE[1]],
        ['1', DEGRADE_MARQUE[2]],
      ]
    : [
        ['0', debut],
        ['1', fin],
      ];
  // Un identifiant par anneau : deux anneaux à l'écran ne doivent pas se partager un dégradé. Les
  // deux-points de `useId` ne passent pas dans un `url(#…)` sur le web.
  const idDegrade = `anneau${useId().replace(/:/g, '')}`;
  const trait = degrade ? `url(#${idDegrade})` : couleurAnneau(value);
  const center = size / 2;

  return (
    <View
      testID={testID}
      style={{ width: size, height: size }}
      accessible
      accessibilityRole="progressbar"
      accessibilityLabel={t('a11y.score', { score: value })}
      accessibilityValue={{ min: 0, max: 100, now: value }}
    >
      <Svg width={size} height={size}>
        {degrade ? (
          <Defs>
            <LinearGradient id={idDegrade} x1="0" y1="0" x2="1" y2="1">
              {arrets.map(([offset, couleur]) => (
                <Stop key={offset} offset={offset} stopColor={couleur} />
              ))}
            </LinearGradient>
          </Defs>
        ) : null}
        <Circle
          cx={center}
          cy={center}
          r={radius}
          stroke={sombre ? colors.pisteSombre : marque ? colors.grey100 : colors.grey200}
          strokeWidth={epaisseur}
          fill="none"
        />
        <Circle
          testID={testID ? `${testID}-progress` : undefined}
          cx={center}
          cy={center}
          r={radius}
          stroke={trait}
          strokeWidth={epaisseur}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          fill="none"
          transform={`rotate(-90 ${center} ${center})`}
        />
      </Svg>
      <View style={styles.center} pointerEvents="none">
        <Text
          testID={testID ? `${testID}-value` : undefined}
          style={[marque ? styles.scorePetit : degrade ? styles.scoreGrand : styles.score, sombre && styles.scoreSombre]}
        >
          {marque ? isolerLtr(`${value}%`) : value}
        </Text>
        {legende ? <Text style={[styles.legende, sombre && styles.legendeSombre]}>{legende}</Text> : null}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  center: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    alignItems: 'center',
    justifyContent: 'center',
  },
  // Bleu nuit et non la couleur de l'anneau : le cyan au-dessus de 85 ne tient pas le contraste AA.
  score: {
    fontSize: typography.h1.size,
    fontWeight: typography.h1.weight,
    color: colors.night,
  },
  scoreGrand: {
    fontSize: typography.display.size,
    lineHeight: typography.display.lineHeight,
    fontWeight: '800',
    color: colors.night,
  },
  // Anneau de la carte d'offre : 14 pt, gras, avec le « % » dans l'anneau de 58 pt.
  scorePetit: { fontSize: 14, lineHeight: 18, fontWeight: '800', color: colors.night },
  scoreSombre: { color: colors.white },
  legende: { fontSize: typography.caption.size, lineHeight: typography.caption.lineHeight, color: colors.grey500 },
  legendeSombre: { color: colors.blancAtenue },
});
